import { useState, useEffect, useCallback, useRef } from "react";
import { aiInsightsService } from "../services/aiInsightsService";

/**
 * Hook to manage AI Insights for an account
 * @param {number|string} accountId - The account ID
 * @param {Object} options - Hook options
 * @param {string} options.template - Template name to use (e.g., 'account_health', 'churn_risk')
 * @returns {Object} AI Insights state and actions
 */
export const useAIInsights = (accountId, options = {}) => {
  const { template } = options;

  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // True while a stale payload is on screen and we are polling for the regenerated one.
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(0);

  // AbortController ref for cleanup
  const abortControllerRef = useRef(null);

  // Abort whatever run is in flight and become the owner of a fresh controller.
  // Every state write that happens after an `await` must check `signal.aborted`
  // on ITS OWN signal first: a superseded run that wakes up late (the re-poll sleeps
  // up to 2s and abort does not wake it) must not touch state the newer run owns —
  // otherwise it can clear the newer run's loading/revalidating flags and briefly
  // render the previous account's summary under the new account's URL.
  // Ownership also means owning the run-scoped flags: the superseded run is barred
  // from clearing them (see the guards below), so the new owner must reset them
  // here — otherwise a flag the old run set stays up under the new run's card.
  const takeOwnership = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setIsRevalidating(false);
    setIsRefreshing(false);
    setIsLoading(false);
    return abortControllerRef.current.signal;
  };

  // Stale-while-revalidate follow-through (PAN-413): the backend served a stale
  // payload and queued a regen. Keep the stale card on screen and swap in the fresh
  // one when it lands. Shared by the initial fetch and the manual refresh — the
  // refresh endpoint returns 202 then the first GET, which is the OLD payload while the
  // regen is still running; when the backend flags that payload stale+refreshing
  // (soft-invalidated or past TTL — the PAN-413 case) a user who clicked Refresh to
  // fix the text would otherwise watch the old text come straight back. A still-fresh
  // payload comes back unflagged and is not re-polled (pre-existing backend behaviour).
  const followThroughIfStale = useCallback(
    async (data, signal) => {
      if (!(data?.stale && data?.refreshing)) return;
      // Yield one macrotask before raising the flag: React 18 batches the
      // setInsights/setIsLoading writes above with this one into a single render,
      // which would mount the live region already containing its text (often not
      // announced). After the yield the card is committed and the status line is a
      // change inside an existing region.
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (signal.aborted) return;
      setIsRevalidating(true);
      try {
        const fresh = await aiInsightsService.pollUntilFresh(accountId, {
          template,
          signal,
        });
        if (signal.aborted) return;
        if (fresh) {
          setInsights(fresh);
          if (fresh.refresh_cooldown_remaining) {
            setCountdown(fresh.refresh_cooldown_remaining);
          }
        } else {
          console.warn(
            "[ai-insights] stale re-poll gave up; showing last payload",
            { accountId },
          );
        }
      } finally {
        // Only the run that still owns the controller may clear the flag.
        if (!signal.aborted) setIsRevalidating(false);
      }
    },
    [accountId, template],
  );

  // Fetch insights with polling support
  const fetchInsights = useCallback(async () => {
    if (!accountId) return;

    const signal = takeOwnership();

    setIsLoading(true);
    setIsGenerating(false);
    setError(null);

    try {
      const data = await aiInsightsService.getInsights(accountId, {
        template,
        signal,
        onGenerating: () => {
          setIsGenerating(true);
          setIsLoading(false); // Switch from loading to generating state
        },
      });
      if (signal.aborted) return;

      setInsights(data);
      setIsGenerating(false);

      // Set countdown if refresh is on cooldown
      if (data.refresh_cooldown_remaining) {
        setCountdown(data.refresh_cooldown_remaining);
      }

      // The card must be on screen before we wait for the regen.
      setIsLoading(false);
      await followThroughIfStale(data, signal);
    } catch (err) {
      // Ignore abort errors — and any error surfacing on a superseded run (e.g. the
      // service's poll timeout thrown after a sleep that abort could not interrupt).
      if (
        signal.aborted ||
        err.message === "Request cancelled" ||
        err.name === "CanceledError"
      ) {
        return;
      }

      console.error("Error fetching AI insights:", err);
      setIsGenerating(false);

      if (err.response?.status === 503) {
        setError("AI insights not available for your team");
      } else if (err.response?.status === 502) {
        setError("AI service temporarily unavailable");
      } else if (err.response?.status !== 401) {
        setError(
          err.response?.data?.error ||
            err.message ||
            "Failed to load AI insights",
        );
      }
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  }, [accountId, template, followThroughIfStale]);

  // Refresh insights with polling support
  const refreshInsights = useCallback(async () => {
    if (!accountId || !insights?.can_refresh || countdown > 0) return;

    const signal = takeOwnership();

    setIsRefreshing(true);
    setIsGenerating(false);
    setError(null);

    try {
      const data = await aiInsightsService.refreshInsights(accountId, {
        template,
        signal,
        onGenerating: () => {
          setIsGenerating(true);
        },
      });
      if (signal.aborted) return;

      setInsights(data);
      setIsGenerating(false);
      setCountdown(0);
      setIsRefreshing(false);
      await followThroughIfStale(data, signal);
    } catch (err) {
      // Ignore abort errors and any error surfacing on a superseded run.
      if (
        signal.aborted ||
        err.message === "Request cancelled" ||
        err.name === "CanceledError"
      ) {
        return;
      }

      console.error("Error refreshing AI insights:", err);
      setIsGenerating(false);

      if (err.response?.status === 429) {
        // Cooldown response - still contains cached insights
        const data = err.response?.data;
        if (data) {
          setInsights(data);
          setCountdown(data.refresh_cooldown_remaining || 0);
        }
        setError(data?.refresh_error || "Please wait before refreshing again");
      } else if (err.response?.status === 502) {
        setError("AI service temporarily unavailable");
      } else if (err.response?.status === 503) {
        setError("AI insights not available for your team");
      } else {
        setError(
          err.response?.data?.error ||
            err.message ||
            "Failed to refresh AI insights",
        );
      }
    } finally {
      if (!signal.aborted) setIsRefreshing(false);
    }
  }, [
    accountId,
    template,
    insights?.can_refresh,
    countdown,
    followThroughIfStale,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  // Initial fetch
  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  // Computed values
  const generatedAt = insights?.generated_at
    ? new Date(insights.generated_at)
    : null;
  const validUntil = insights?.valid_until
    ? new Date(insights.valid_until)
    : null;
  const now = new Date();

  const minutesAgo = generatedAt
    ? Math.floor((now - generatedAt) / 60000)
    : null;
  const minutesRemaining = validUntil
    ? Math.max(0, Math.floor((validUntil - now) / 60000))
    : null;

  const isStale = validUntil ? now > validUntil : false;
  const canRefresh =
    (insights?.can_refresh && countdown === 0) || countdown === 0;

  // Format time ago text
  const getTimeAgoText = () => {
    if (minutesAgo === null) return null;
    if (minutesAgo < 1) return "Just now";
    if (minutesAgo === 1) return "1 min ago";
    if (minutesAgo < 60) return `${minutesAgo} min ago`;
    const hoursAgo = Math.floor(minutesAgo / 60);
    if (hoursAgo === 1) return "1 hour ago";
    return `${hoursAgo} hours ago`;
  };

  // Format refresh text
  const getRefreshText = () => {
    if (minutesRemaining === null) return null;
    if (minutesRemaining <= 0) return "Refreshing soon...";
    if (minutesRemaining === 1) return "Auto-refreshes in 1 min";
    if (minutesRemaining < 60)
      return `Auto-refreshes in ${minutesRemaining} min`;
    const hoursRemaining = Math.floor(minutesRemaining / 60);
    if (hoursRemaining === 1) return "Auto-refreshes in 1 hour";
    return `Auto-refreshes in ${hoursRemaining} hours`;
  };

  return {
    insights,
    isLoading,
    isGenerating,
    isRefreshing,
    isRevalidating,
    error,
    countdown,
    canRefresh,
    isStale,
    timeAgoText: getTimeAgoText(),
    refreshText: getRefreshText(),
    fetchInsights,
    refreshInsights,
  };
};
